import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["countries", "disabled", "error", "formatAssetsUrl", "showIcon", "inputProps", "label", "placeholder", "currentLang", "selectProps", "name", "value", "onChange", "onBlur", "handleTriggerValidation", "dependantOn", "dataTestId", "useTooltip", "isAltStyle"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box, Flex, FormControl, FormErrorMessage, FormLabel, Image, Input, InputGroup, InputLeftElement, Text, useDisclosure, useTheme, useOutsideClick } from '@chakra-ui/react';
import { CountryCode, ShortCountry } from '@whitbread-eos/api';
import { getInputTypographyOverride, getTextStyleTypographyOverride, useSemanticTypography } from '@whitbread-eos/utils';
import dynamic from 'next/dynamic';
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Error24, Success24 } from '../../assets/icons';
import ChevronDown from '../../assets/icons/ChevronDown';
import ChevronUp from '../../assets/icons/ChevronUp';
import { formatDataTestId } from '../../utils/formatters';
import Icon from '../Icon';
import CountriesList from './CountriesList';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Tooltip = dynamic(_asyncToGenerator(function* () {
  var _yield$import = yield import('../Tooltip/Tooltip.component'),
    Tooltip = _yield$import.default;
  return {
    default: Tooltip
  };
}), {
  ssr: false
});
function PhoneInput(_ref2, forwardedRef) {
  var _ref2$countries = _ref2.countries,
    countries = _ref2$countries === void 0 ? [] : _ref2$countries,
    disabled = _ref2.disabled,
    error = _ref2.error,
    _ref2$formatAssetsUrl = _ref2.formatAssetsUrl,
    formatAssetsUrl = _ref2$formatAssetsUrl === void 0 ? url => url : _ref2$formatAssetsUrl,
    showIcon = _ref2.showIcon,
    inputProps = _ref2.inputProps,
    label = _ref2.label,
    placeholder = _ref2.placeholder,
    currentLang = _ref2.currentLang,
    selectProps = _ref2.selectProps,
    name = _ref2.name,
    _ref2$value = _ref2.value,
    value = _ref2$value === void 0 ? {
      countryCode: '',
      dialingCode: '',
      phone: ''
    } : _ref2$value,
    onChange = _ref2.onChange,
    onBlur = _ref2.onBlur,
    handleTriggerValidation = _ref2.handleTriggerValidation,
    dependantOn = _ref2.dependantOn,
    dataTestId = _ref2.dataTestId,
    _ref2$useTooltip = _ref2.useTooltip,
    useTooltip = _ref2$useTooltip === void 0 ? false : _ref2$useTooltip,
    _ref2$isAltStyle = _ref2.isAltStyle,
    isAltStyle = _ref2$isAltStyle === void 0 ? false : _ref2$isAltStyle,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var getTypographyProps = useSemanticTypography();
  var theme = useTheme();
  var ref = useRef(null);
  var phoneInputRef = useRef(null);
  var prevIsOpenRef = useRef(false);
  var _useDisclosure = useDisclosure(),
    isOpen = _useDisclosure.isOpen,
    onToggle = _useDisclosure.onToggle,
    onClose = _useDisclosure.onClose;
  var displayIcon = showIcon && !disabled;
  var displayLabel = label && !disabled;
  var isValid = !error && value.phone;
  var baseDataTestId = dataTestId !== null && dataTestId !== void 0 ? dataTestId : 'PhoneInput';
  var currentCountrySite = currentLang === CountryCode.DE ? ShortCountry.DE : ShortCountry.GB;
  var inputTypographyOverride = getInputTypographyOverride(inputProps, theme === null || theme === void 0 ? void 0 : theme.textStyles);
  var altErrorTypography = getTypographyProps({
    fontSize: 'xs'
  }, {
    textStyle: 'body-s-regular'
  });
  var errorTypography = getTypographyProps({}, {
    textStyle: 'body-s-regular'
  });
  var altErrorTypographyProps = _objectSpread(_objectSpread({}, altErrorTypography), getTextStyleTypographyOverride(altErrorTypography, theme === null || theme === void 0 ? void 0 : theme.textStyles));
  var errorTypographyProps = _objectSpread(_objectSpread({}, errorTypography), getTextStyleTypographyOverride(errorTypography, theme === null || theme === void 0 ? void 0 : theme.textStyles));
  var labelStyleAlt = () => {
    return {
      w: 'fit-content',
      fontSize: 'md',
      align: 'center',
      fontWeight: 'bold',
      zIndex: '1',
      color: '#333333'
    };
  };
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      setTimeout(() => {
        var _phoneInputRef$curren;
        (_phoneInputRef$curren = phoneInputRef.current) === null || _phoneInputRef$curren === void 0 || _phoneInputRef$curren.focus();
      }, 0);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);
  useOutsideClick({
    ref: ref,
    handler: onClose
  });
  var sortedCountries = getSortedCountries(countries);
  var country = useMemo(() => countries.find(country => country.countryCode === value.countryCode && country.dialingCode === value.dialingCode), [countries, value.countryCode, value.dialingCode]);
  var handleCountryChange = useCallback(item => {
    onChange(_objectSpread(_objectSpread({}, value), {}, {
      countryCode: item.countryCode,
      dialingCode: item.dialingCode
    }));
    onClose();
  }, [value, onChange, onClose]);
  var handleNumberChange = useCallback(event => {
    onChange(_objectSpread(_objectSpread({}, value), {}, {
      phone: event.target.value
    }));
  }, [value, onChange]);
  var handleOnBlur = useCallback(() => {
    if (dependantOn && handleTriggerValidation) {
      handleTriggerValidation(dependantOn);
    }
    onBlur();
  }, [onBlur, dependantOn, handleTriggerValidation]);
  var handleKeyDown = useCallback(event => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      onClose();
    }
  }, [disabled, onToggle, isOpen, onClose]);
  function getSortedCountries(data) {
    if (!data || (data === null || data === void 0 ? void 0 : data.length) === 0) {
      return [];
    }
    var sortedData = [...data].sort(function (a, b) {
      if (a.countryCode === currentCountrySite) {
        return -1;
      }
      if (b.countryCode === currentCountrySite) {
        return 0;
      }
      if (a.countryName < b.countryName) {
        return -1;
      }
      if (a.countryName > b.countryName) {
        return 1;
      }
      return 0;
    });
    return sortedData;
  }
  return _jsxs(FormControl, _objectSpread(_objectSpread({
    isInvalid: !!error,
    ref: ref
  }, _objectSpread({}, rest)), {}, {
    children: [isAltStyle && _jsx(FormLabel, _objectSpread(_objectSpread({
      "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-label"))
    }, labelStyleAlt()), {}, {
      htmlFor: name,
      children: label
    })), _jsx(Tooltip, _objectSpread(_objectSpread({}, tooltipStyle), {}, {
      description: error !== null && error !== void 0 ? error : '',
      variant: "inlineError",
      placement: "bottom-start",
      isOpen: !!error && useTooltip,
      svg: _jsx(Error24, {}),
      children: _jsxs(InputGroup, {
        children: [_jsxs(InputLeftElement, _objectSpread(_objectSpread({
          "data-testid": formatDataTestId(baseDataTestId, 'countrySelector')
        }, selectorStyle({
          disabled
        })), {}, {
          role: "button",
          type: "button",
          tabIndex: disabled ? -1 : 0,
          onClick: onToggle,
          onKeyDown: handleKeyDown,
          "aria-label": country ? "Change country code. Currently ".concat(country.countryName, " ").concat(country.dialingCode) : 'Select country code',
          "aria-expanded": isOpen,
          children: [country && _jsxs(Flex, {
            "data-testid": formatDataTestId(baseDataTestId, 'countryFlag'),
            children: [_jsx(Image, {
              borderRadius: "full",
              boxSize: "1.5rem",
              src: formatAssetsUrl(country.flagSrc),
              alt: country.countryName,
              title: country.countryName
            }), _jsx(Text, {
              as: "span",
              ml: 1,
              whiteSpace: "nowrap",
              children: country.dialingCode
            })]
          }), _jsx(Icon, {
            mx: "2",
            svg: isOpen ? _jsx(ChevronUp, {
              "aria-label": "chevron-icon-up"
            }) : _jsx(ChevronDown, {
              "aria-label": "chevron-icon-down"
            })
          })]
        })), !isAltStyle && displayLabel && _jsx(FormLabel, _objectSpread(_objectSpread({
          "data-testid": formatDataTestId(baseDataTestId, "".concat(name, "-label")),
          pos: "absolute"
        }, labelStyle(error, value)), {}, {
          htmlFor: name,
          children: label
        })), _jsx(Input, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
          id: name,
          name: name,
          "aria-label": label || placeholder || 'Phone number',
          disabled: disabled,
          isInvalid: !!error,
          placeholder: !isAltStyle ? placeholder !== null && placeholder !== void 0 ? placeholder : label : undefined,
          ref: el => {
            phoneInputRef.current = el;
            if (typeof forwardedRef === 'function') {
              forwardedRef(el);
            } else if (forwardedRef) {
              forwardedRef.current = el;
            }
          },
          type: "tel",
          value: value.phone
        }, isAltStyle ? inputStyleAlt(error) : inputStyle(error)), {}, {
          onChange: handleNumberChange,
          onBlur: handleOnBlur
        }, inputProps), inputTypographyOverride), {}, {
          "data-testid": formatDataTestId(baseDataTestId, 'phoneNumber')
        })), displayIcon && error && _jsx(Box, _objectSpread(_objectSpread({}, iconStyle), {}, {
          "data-testid": formatDataTestId(baseDataTestId, 'inputIconError'),
          children: _jsx(Error24, {})
        })), displayIcon && isValid && _jsx(Box, _objectSpread(_objectSpread({}, iconStyle), {}, {
          "data-testid": formatDataTestId(baseDataTestId, 'inputIconSuccess'),
          children: _jsx(Success24, {})
        }))]
      })
    })), isAltStyle && error && !useTooltip && _jsx(FormErrorMessage, _objectSpread(_objectSpread({
      ml: "xs"
    }, altErrorTypographyProps), {}, {
      children: error
    })), !isAltStyle && error && !useTooltip && _jsx(FormErrorMessage, _objectSpread(_objectSpread({
      ml: "md"
    }, errorTypographyProps), {}, {
      children: error
    })), isOpen ? _jsx(CountriesList, _objectSpread({
      formatAssetsUrl: formatAssetsUrl,
      options: sortedCountries,
      onChange: handleCountryChange,
      onClose: onClose
    }, selectProps)) : null]
  }));
}
var inputStyle = error => {
  return {
    h: '3.5rem',
    focusBorderColor: 'primary',
    pl: '6.75em',
    borderColor: 'var(--chakra-colors-lightGrey1)',
    _placeholder: {
      color: 'var(--chakra-colors-darkGrey2)'
    },
    _hover: {
      borderColor: 'none'
    },
    _focus: {
      zIndex: '0',
      borderWidth: '2px',
      borderColor: error ? 'error' : 'primary'
    },
    _autofill: {
      boxShadow: '0 0 0 1000px #FFFFFF inset'
    }
  };
};
var inputStyleAlt = error => {
  return {
    h: '3rem',
    backgroundColor: 'var(--chakra-colors-gray-100)',
    focusBorderColor: error ? 'error' : 'primary',
    zIndex: 0,
    pl: '6.75em',
    borderWidth: '0px',
    borderColor: 'var(--chakra-colors-lightGrey1)',
    _hover: {
      borderColor: 'none'
    },
    _focus: {
      zIndex: '0',
      borderWidth: '1px',
      borderColor: error ? 'error' : 'primary'
    },
    _placeholder: {
      color: 'darkGrey2',
      _focus: {
        color: 'success'
      },
      _hover: {
        color: 'primary'
      }
    },
    _autofill: {
      boxShadow: '0 0 0 1000px #FFFFFF inset'
    }
  };
};
var selectorStyle = _ref3 => {
  var disabled = _ref3.disabled;
  return _objectSpread({
    cursor: 'pointer',
    h: 'full',
    justifyContent: 'flex-end',
    pl: '2',
    width: '6.5em'
  }, disabled ? {
    opacity: '0.4',
    pointerEvents: 'none'
  } : {});
};
var iconStyle = {
  ml: 'md',
  alignSelf: 'center'
};
var tooltipStyle = {
  h: '2.25rem',
  display: 'flex',
  alignContent: 'center'
};
var labelStyle = (error, value) => {
  return {
    h: '1.25rem',
    w: 'fit-content',
    fontSize: 'sm',
    align: 'center',
    px: 'xs',
    ml: '0.750rem',
    top: '-0.625rem',
    backgroundColor: 'baseWhite',
    zIndex: '1',
    color: error ? 'error' : 'darkGrey1',
    display: value !== null && value !== void 0 && value.phone ? 'block' : 'none',
    _focus: {
      color: error ? 'error' : 'primary',
      display: 'block'
    }
  };
};
export default memo(forwardRef(PhoneInput));